/**
 * CL-ACCESS-008B — Project file display title (plain text, admin-managed).
 * Does not rename Storage objects. Not used for entitlement or file identity.
 */

function safeStr(value) {
  return String(value ?? "").trim();
}

/** Max length for persisted displayTitle (plain text). */
export const PROJECT_FILE_DISPLAY_TITLE_MAX = 160;

/**
 * Normalize admin-entered display title. Blank/whitespace-only → "".
 * Rejects control characters; does not interpret HTML.
 */
export function normalizeProjectFileDisplayTitle(raw) {
  const cleaned = safeStr(raw)
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";
  return cleaned.slice(0, PROJECT_FILE_DISPLAY_TITLE_MAX);
}

/** Persistable value or null when blank (omit/clear). */
export function serializeProjectFileDisplayTitle(raw) {
  const normalized = normalizeProjectFileDisplayTitle(raw);
  return normalized || null;
}

export function projectFileFilename(file) {
  return (
    safeStr(file?.name) ||
    safeStr(file?.fileName) ||
    safeStr(file?.originalFileName) ||
    safeStr(file?.originalName) ||
    ""
  );
}

export function projectFileDisplayTitle(file) {
  return normalizeProjectFileDisplayTitle(file?.displayTitle);
}

/**
 * Presentation model:
 * - titled: primary = displayTitle, secondary = filename (when different)
 * - untitled: primary = filename once, secondary = null
 */
export function buildProjectFileTitlePresentation(file) {
  const filename = projectFileFilename(file);
  const title = projectFileDisplayTitle(file);
  if (title && filename && title.toLowerCase() !== filename.toLowerCase()) {
    return {
      primary: title,
      secondary: filename,
      hasDistinctTitle: true,
      searchText: `${title} ${filename}`.trim(),
    };
  }
  if (title && !filename) {
    return { primary: title, secondary: null, hasDistinctTitle: true, searchText: title };
  }
  return {
    primary: filename || "Untitled file",
    secondary: null,
    hasDistinctTitle: false,
    searchText: filename,
  };
}

/** Suggest a human title from a filename without aggressive renaming. */
export function suggestDisplayTitleFromFilename(filename) {
  const base = safeStr(filename).replace(/[/\\]/g, " ").trim();
  if (!base) return "";
  const withoutExt = base.replace(/\.[A-Za-z0-9]{1,8}$/, "");
  const softened = withoutExt.replace(/[_]+/g, " ").replace(/\s+/g, " ").trim();
  return normalizeProjectFileDisplayTitle(softened || withoutExt || base);
}

export function projectFileMatchesSearchQuery(file, query) {
  const q = safeStr(query).toLowerCase();
  if (!q) return true;
  const hay = buildProjectFileTitlePresentation(file).searchText.toLowerCase();
  return hay.includes(q);
}
